import type {ReactElement} from 'react';
import React, { useEffect, useState} from 'react';
import {ScrollablePills} from '@Components/scrollable-pills';
import {BaseOrderedListThumbnails} from '@Panels/poster-editor-item-list-panel/components/base-ordered-list-thumbnails';
import {BaseUnorderedListThumbnails} from '@Panels/poster-editor-item-list-panel/components/base-unordered-list-thumbnails';
import type {LIST_STYLE, ORDERED_LIST_STYLE, TextListObject, UNORDERED_LIST_STYLE} from '@PosterWhiteboard/items/text-item/text-list';
import { LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import {ContextualMenu} from '@Components/contextual-menu';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import type {ColorFillStorage} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import { ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {getColorFillTypeFromFillType, getColorObjectArrayFromFillColor} from '@Utils/color.util';
import styles from './base-list-mobile-options.module.scss';

interface BaseListMobileOptionsProps {
  panelId: string;
  onClickColorFill(): void;
  listObject: TextListObject;
  updateListObject(type: LIST_TYPES, styleToApply?: LIST_STYLE): void;
  hasList?: boolean;
}

export enum ListOptions {
  BULLETS = 'BULLETS',
  NUMBERS = 'NUMBERS',
}

export function BaseListMobileOptions({hasList = false, ...props}: BaseListMobileOptionsProps): ReactElement | null {
  const [listOptionToShow, setListOptionToShow] = useState(props.listObject.type === LIST_TYPES.ORDERED ? ListOptions.NUMBERS : ListOptions.BULLETS);

  useEffect((): void => {
    let height = 208;
    if (props.listObject.type !== LIST_TYPES.NONE) {
      height += 32 + 16;
    }

    updateOptionsPanelHeight(props.panelId, `${height}px`);
  }, [props.listObject.type]);

  const applyNone = (): void => {
    if (props.listObject.type === LIST_TYPES.NONE) {
      return;
    }
    props.updateListObject(LIST_TYPES.NONE);
  };
  const applyOrderedListType = (type: ORDERED_LIST_STYLE): void => {
    props.updateListObject(LIST_TYPES.ORDERED, type);
  };
  const applyUnorderedListType = (type: UNORDERED_LIST_STYLE): void => {
    props.updateListObject(LIST_TYPES.UNORDERED, type);
  };

  const getSelectedIdForUnordered = (): UNORDERED_LIST_STYLE | null => {
    if (props.listObject.type !== LIST_TYPES.UNORDERED) {
      return null;
    }

    return props.listObject.style as UNORDERED_LIST_STYLE;
  };
  const getSelectedIdForOrdered = (): ORDERED_LIST_STYLE | null => {
    if (props.listObject.type !== LIST_TYPES.ORDERED) {
      return null;
    }

    return props.listObject.style as ORDERED_LIST_STYLE;
  };

  const getColorData = (): Array<ColorFillStorage> => {
    return [
      {
        id: 'text-list-color-object',
        fillType: getColorFillTypeFromFillType(props.listObject.fill.fillType),
        color: getColorObjectArrayFromFillColor(props.listObject.fill.fillColor),
      },
    ];
  };

  const onClickHandler = (): void => {
    props.onClickColorFill();
  };

  const getListOptionsToShow = (): ReactElement => {
    switch (listOptionToShow) {
      case ListOptions.NUMBERS:
        return (
          <BaseOrderedListThumbnails
            className={`${hasList ? 'spacing-m-t-4' : ''} ${styles.thumbnailContainer}`}
            onClickNone={applyNone}
            onClickListOption={applyOrderedListType}
            selectedId={getSelectedIdForOrdered()}
          />
        );
      case ListOptions.BULLETS:
      default:
        return (
          <BaseUnorderedListThumbnails
            className={`${hasList ? 'spacing-m-t-4' : ''} ${styles.thumbnailContainer}`}
            onClickNone={applyNone}
            onClickListOption={applyUnorderedListType}
            selectedId={getSelectedIdForUnordered()}
          />
        );
    }
  };

  const getColorOption = (): ReactElement | null => {
    if (!hasList) {
      return null;
    }
    return (
      <ContextualMenuColorFillOption
        className="spacing-p-l-4 spacing-p-r-4"
        id="text-list-color-option"
        label={window.i18next.t('pmwjs_color')}
        data={getColorData()}
        onClick={onClickHandler}
      />
    );
  };

  return (
    <ContextualMenu id="base-list-mobile-options" hasMobileMorphingProperties includeLeftRightPadding={false} contentClassName="spacing-p-t-1 spacing-p-b-4">
      {getColorOption()}
      {getListOptionsToShow()}
      <ScrollablePills
        className="spacing-m-t-4"
        data={[
          {
            id: ListOptions.BULLETS,
            text: window.i18next.t('pmwjs_bullets'),
            icon: 'icon-list',
          },
          {
            id: ListOptions.NUMBERS,
            text: window.i18next.t('pmwjs_numbers'),
            icon: 'icon-list-ordered',
          },
        ]}
        selectedItemId={listOptionToShow}
        onClick={(id: ListOptions): void => {
          setListOptionToShow(id);
        }}
      />
    </ContextualMenu>
  );
}
